import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';

import {FuseConfigService} from '@fuse/services/config.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';

import {select, Store} from '@ngrx/store';
import {authLogout, selectUserProfile} from '../../../../@aes/core/core.module';
import {UserProfile} from '../../../../@aes/core/auth/auth.models';
import {MatDialog} from '@angular/material/dialog';
import {ChangePasswordComponent} from '../../../auth/change-password/change-password.component';
import {selectChangePassword} from '../../../auth/change-password/state-management/change-password.selectors';
import {changePasswordSave} from '../../../auth/change-password/state-management/change-password.actions';
import {FuseTranslationLoaderService} from '../../../../@fuse/services/translation-loader.service';
import {locale as english} from './i18n/en';
import {locale as vietnam} from './i18n/vn';
import { AppSwitcherDialogComponent } from 'app/shared/app-switcher/app-switcher-dialog/app-switcher-dialog.component';
import { AppSwitcherService } from 'app/shared/app-switcher/app-switcher.service';


@Component({
    selector     : 'toolbar',
    templateUrl  : './toolbar.component.html',
    styleUrls    : ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToolbarComponent implements OnInit, OnDestroy
{
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];
    isShowAppSwitcher = false;

    _userProfile$: Observable<UserProfile> = this._store.pipe(select(selectUserProfile));

    // Private
    private _unsubscribeAll: Subject<any>;
    private readonly destroy$ = new Subject<void>();

    /**
     * Constructor
     *
     * @param _store
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private _store: Store<any>,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        public matDialog: MatDialog,
        protected _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _appSwitcherService: AppSwitcherService,
    )
    {
        // Set the defaults
        this.userStatusOptions = [
            {
                title: 'Online',
                icon : 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon : 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon : 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon : 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon : 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id   : 'vn',
                title: 'Tiếng Việt',
                flag : 'vn'
            },
            {
                id   : 'en',
                title: 'English',
                flag : 'us'
            }
        ];

        // Set the private defaults
        this._unsubscribeAll = new Subject();
        this._fuseTranslationLoaderService.loadTranslations(english, vietnam);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, {id: this._translateService.currentLang});

        this._appSwitcherService
        .getModuleState()
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.isShowAppSwitcher = response.length > 1;
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void
    {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void
    {
        // Do your search here...
        console.log(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void
    {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
    }

    onLogoutClick(): void {
        this._store.dispatch(authLogout());
    }

    onChangePasswordClick(): void {
        const state = this._store.select(selectChangePassword).pipe().subscribe(
            (rs) => {
                const dialogRef = this.matDialog.open(ChangePasswordComponent, {
                    id: 'change-password-dialog',
                    autoFocus : false,
                    data: {
                        data: rs
                    }
                });
                dialogRef.afterOpened().subscribe(() => {
                    state.unsubscribe();
                });
                dialogRef.afterClosed().subscribe(() => {
                    this._store.dispatch(changePasswordSave({
                        currentPassword: dialogRef.componentInstance.changePasswordForm.get('oldPassword').value,
                        newPassword: dialogRef.componentInstance.changePasswordForm.get('newPassword').value,
                        confirmPassword: dialogRef.componentInstance.changePasswordForm.get('confirmPassword').value,
                    }));
                });
            }
        );
    }

    showAppSwitcher(): void {
            const dialogRef = this.matDialog.open(AppSwitcherDialogComponent, {autoFocus: false});
    }

}
